import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/use-language";
import { ORDER_CTAS_ENABLED } from "@/config/orderCtas";
import logoWide from "@/assets/logo-wide.webp";

export function Navbar({ hasBanner = false }: { hasBanner?: boolean } = {}) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { currentLang, switchLanguage, localizedPath } = useLanguage();

  const navItems = [
    { label: t("nav.whyPraedigo"), href: "/#hero" },
    { label: t("nav.product"), href: "/product" },
    { label: t("nav.newsEvents"), href: "/comorbidities" },
    { label: t("nav.access"), href: "/praedigo-access" },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isActive = (href: string) => {
    const fullPath = localizedPath(href);
    if (href.startsWith("/#")) {
      return location.pathname === `/${currentLang}` && location.hash === href.slice(1);
    }
    return location.pathname === fullPath;
  };

  return (
    <header
      className={`fixed ${hasBanner ? "top-10" : "top-0"} left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-md shadow-clinical border-b border-border" : ""
      }`}
      style={{ backgroundColor: "#FFFFFF" }}
    >
      <div className="container mx-auto flex items-center justify-between h-16 px-4 lg:px-8">
        <Link to={localizedPath("/")} className="flex items-center">
          <img src={logoWide} alt="Praedigo" className="h-7" width="96" height="28" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={localizedPath(item.href)}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                isActive(item.href)
                  ? "text-primary bg-secondary"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {ORDER_CTAS_ENABLED && (
            <Link to={localizedPath("/bestellung")}>
              <button className="rounded-full px-5 py-2 text-sm font-medium transition-colors hover:bg-[#F4F4F4]" style={{ color: "#8B9DFA" }}>
                {t("nav.testFree")}
              </button>
            </Link>
          )}
          <button
            onClick={() => switchLanguage(currentLang === "en" ? "de" : "en")}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground rounded-md hover:bg-secondary/60 transition-colors"
            aria-label={currentLang === "en" ? "Switch language to DE" : "Switch language to EN"}
          >
            <Globe size={16} />
            <span className="uppercase">{currentLang === "en" ? "DE" : "EN"}</span>
          </button>
          <Link to={localizedPath("/contact")}>
            <Button style={{ backgroundColor: "#6D84F9" }} className="rounded-full px-6 font-medium text-sm shadow-none text-white hover:opacity-90 transition-opacity">
              {t("nav.contactUs")}
            </Button>
          </Link>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          {ORDER_CTAS_ENABLED && (
            <Link to={localizedPath("/bestellung")}>
              <button
                className="rounded-full px-4 py-1.5 text-xs font-medium text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: "#6D84F9" }}
              >
                {t("nav.testFree")}
              </button>
            </Link>
          )}
          <button
            className="p-2 text-foreground"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-card border-t border-border shadow-clinical-lg">
          <div className="container mx-auto px-4 py-4 flex flex-col gap-2">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={localizedPath(item.href)}
                className="px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md transition-colors"
              >
                {item.label}
              </Link>
            ))}
            {ORDER_CTAS_ENABLED && (
              <Link to={localizedPath("/bestellung")} className="mt-2">
                <button className="w-full rounded-full px-5 py-2 text-sm font-medium transition-colors hover:bg-[#F4F4F4]" style={{ color: "#8B9DFA" }}>
                  {t("nav.testFree")}
                </button>
              </Link>
            )}
            <button
              onClick={() => switchLanguage(currentLang === "en" ? "de" : "en")}
              className="flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary rounded-md transition-colors"
            >
              <Globe size={16} />
              {currentLang === "en" ? "Deutsch" : "English"}
            </button>
            <Link to={localizedPath("/contact")} className="mt-2">
              <Button style={{ backgroundColor: "#6D84F9" }} className="w-full rounded-full px-6 font-medium text-sm shadow-none text-white hover:opacity-90 transition-opacity">
                {t("nav.contactUs")}
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
